import { memo, useCallback } from 'react';

import { ExternalLinkIcon, Pencil, StarIcon } from 'lucide-react';
import { twJoin, twMerge } from 'tailwind-merge';

import { FilledStar, PrimaryLink, TableCell, TableRow, Text } from '@/_components';
import { ActionButton, DeleteButton } from '@/_containers';
import type { VendorDetailsFormSchema } from '@/_pages/settings/inventory-kits/shared/schemas/vendor-details';
import type { InventoryVendorQuerySchema } from '@/_pages/settings/inventory-vendors/shared/schemas';
import { currencyInput } from '@/_schemas/generic';
import { currencyFormat, useBoolean } from '@/_shared';

import { EditVendorDetailModal } from './edit-vendor-detail-modal';
import { NotesHoverCard } from './notes-hover-card';

type InventoryVendorItemProps = {
  vendorDetail: VendorDetailsFormSchema;
  index: number;
  isEditing?: boolean;
  onDelete: (vendorDetail: InventoryVendorQuerySchema) => void;
  className?: string;
  setVendorType?: (vendorDetail: VendorDetailsFormSchema, isPrimary: boolean) => void;
};

function InventoryVendorItemBase({
  vendorDetail,
  index,
  isEditing = false,
  onDelete,
  className,
  setVendorType,
}: InventoryVendorItemProps) {
  const [isEditingDetail, editingDetailState] = useBoolean(false);
  const isPrimary = vendorDetail.vendor.pivot?.type === 'primary';

  const handleDelete = useCallback(() => {
    onDelete(vendorDetail.vendor);
  }, [onDelete, vendorDetail]);

  const handleVendorTypeToggle = useCallback(() => {
    setVendorType?.(vendorDetail, isPrimary);
  }, [setVendorType, vendorDetail, isPrimary]);

  return (
    <>
      <TableRow className={twMerge('align-center hover:bg-transparent dark:hover:bg-transparent', className)}>
        <TableCell>
          <PrimaryLink
            to="/settings/inventory-vendors/$id"
            params={{ id: `${vendorDetail.vendor.id}` }}
            state={{ naturalBack: true }}
          >
            <div className="text-accent text-sm font-normal dark:text-white">{vendorDetail.vendor?.name || '—'}</div>
          </PrimaryLink>
        </TableCell>
        <TableCell>
          <div className="text-accent-light dark:text-accent-x-light text-sm font-normal">
            {vendorDetail.vendor_sku || '—'}
          </div>
        </TableCell>
        <TableCell className="flex justify-center text-center">
          {vendorDetail.vendor_url ? (
            <ActionButton
              icon={ExternalLinkIcon}
              title={vendorDetail.vendor_url}
              className="text-accent-light dark:text-accent-x-light m-auto text-sm font-normal"
              onClick={() => {
                window.open(vendorDetail.vendor_url, '_blank');
              }}
            />
          ) : (
            <Text className="text-accent-light dark:text-accent-x-light text-sm font-normal">—</Text>
          )}
        </TableCell>

        <TableCell className="text-center">
          <NotesHoverCard text={vendorDetail.vendor_note} />
        </TableCell>

        <TableCell>
          <div className="text-accent-light dark:text-accent-x-light text-sm font-normal">
            {currencyFormat(currencyInput.formToUpdateConverter(`${vendorDetail?.last_price ?? ''}`) ?? '')}
          </div>
        </TableCell>

        <TableCell className={twJoin('flex items-center justify-end gap-x-1 text-right', isEditing ? 'w-28' : '')}>
          <ActionButton
            icon={isPrimary ? FilledStar : StarIcon}
            title={isPrimary ? 'Primary Vendor' : 'Make Primary'}
            disabled={!isEditing}
            onClick={isEditing ? handleVendorTypeToggle : undefined}
            className="opacity-100"
            aria-label="Vendor"
          />
          {isEditing && (
            <>
              <ActionButton icon={Pencil} title="Edit Vendor Details" onClick={editingDetailState.setTrue} />
              <DeleteButton
                type="icon"
                size="sm"
                title="Remove Vendor?"
                text="The Vendor record will not be deleted; only its association will be removed."
                yesButton="Yes, Remove"
                aria-label="delete-vendor"
                callback={handleDelete}
              />
            </>
          )}
        </TableCell>
      </TableRow>

      <EditVendorDetailModal
        index={index}
        vendorDetail={vendorDetail}
        open={isEditingDetail}
        onClose={editingDetailState.setFalse}
      />
    </>
  );
}

export const InventoryVendorItem = memo(InventoryVendorItemBase);
